import { type JSX, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import Toggle from '@cloudscape-design/components/toggle';
import {
  ADMIN_NOTIFICATION_EVENTS,
  AdminNotificationPrefsSchema,
  resolveAdminNotificationPref,
  type AdminNotificationChannel,
  type AdminNotificationEvent,
  type AdminNotificationPrefsMap,
} from '@etiquetachef/types';
import { useAuth } from '@/auth/AuthProvider';
import { firestore } from '@/lib/firebase';
import { useWebPush } from '@/lib/useWebPush';
import { reportNonFatal, serializeError } from '@/lib/errorReport';
import { assertCanWrite } from '@etiquetachef/ui-shared/online';

const PREFS_COLLECTION = 'adminNotificationPrefs';

type ResolvedEvents = Record<AdminNotificationEvent, { email: boolean; push: boolean }>;

/** Resolve TODOS os eventos aplicando defaults (tudo ligado quando ausente). */
function resolveAll(map: AdminNotificationPrefsMap | undefined): ResolvedEvents {
  const out = {} as ResolvedEvents;
  for (const event of ADMIN_NOTIFICATION_EVENTS) {
    out[event] = resolveAdminNotificationPref(event, map);
  }
  return out;
}

type Row = { event: AdminNotificationEvent };
const ROWS: Row[] = ADMIN_NOTIFICATION_EVENTS.map((event) => ({ event }));

/**
 * Card "Notificações de operação" do console super-admin: para cada evento de
 * negócio (nova empresa, pagamento, cancelamento/refund, suspensão), dois
 * toggles independentes — e-mail e push — persistidos por super-admin em
 * `/adminNotificationPrefs/{uid}`. Escrita direta (setDoc) gateada por Rules;
 * o fan-out (`notifyAdmins`) lê essas prefs no servidor. Default = tudo ligado.
 *
 * Origem: sem issue — commit c1d548e4c, 2026-06-19 13:49 +0200 (preferências
 * de notificação de ops por super-admin).
 *
 * Contrato: assina o próprio doc (`uid` do `useAuth`); toggle grava na hora,
 * com atualização otimista e reversão em falha. Falha de leitura ou escrita →
 * `reportNonFatal` (`admin_notif_prefs_subscribe_failed` /
 * `admin_notif_prefs_save_failed`) + alerta inline.
 *
 * Usado por: `routes/NotificationPrefsPage.tsx`.
 *
 * Arquitetura: escrita direta autorizada por `match /adminNotificationPrefs/{uid}`
 * (só o próprio uid, só as chaves `events` e `updatedAt`); quem lê para decidir o
 * envio é `notifyAdmins` (`apps/functions/src/lib/adminNotify.ts`), via Admin SDK.
 * Spec: `docs/screens/admin-notification-prefs.md`.
 *
 * Dados: `adminNotificationPrefs/{uid}` =
 * `{ events: { <EVENTO>: { email: true, push: false }, … }, updatedAt: serverTimestamp }`,
 * formato `AdminNotificationPrefsSchema` (`@etiquetachef/types`). Evento ou
 * canal ausente = ligado (`resolveAdminNotificationPref`). O toggle grava o mapa
 * INTEIRO já resolvido, então o primeiro clique materializa os defaults.
 */
export function NotificationPrefsCard(): JSX.Element {
  const { t } = useTranslation();
  const { status } = useAuth();
  const uid = status.kind === 'AUTHORIZED' ? status.user.uid : null;
  const webPush = useWebPush();

  const [events, setEvents] = useState<ResolvedEvents>(() => resolveAll(undefined));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (uid === null) return;
    const ref = doc(firestore, PREFS_COLLECTION, uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const parsed = AdminNotificationPrefsSchema.safeParse(snap.exists() ? snap.data() : {});
        const map = parsed.success ? parsed.data.events : undefined;
        setEvents(resolveAll(map));
        setLoading(false);
      },
      (err) => {
        reportNonFatal('admin_notif_prefs_subscribe_failed', { ...serializeError(err) });
        setError(t('admin.notificationPrefs.loadError'));
        setLoading(false);
      },
    );
    return unsub;
  }, [uid, t]);

  /** Grava o mapa inteiro (merge) com otimismo; reverte e reporta em falha. */
  async function persist(next: ResolvedEvents): Promise<void> {
    if (uid === null) return;
    const prev = events;
    setEvents(next); // otimista
    setError(null);
    try {
      assertCanWrite();
      await setDoc(
        doc(firestore, PREFS_COLLECTION, uid),
        { events: next, updatedAt: serverTimestamp() },
        { merge: true },
      );
    } catch (err) {
      reportNonFatal('admin_notif_prefs_save_failed', { ...serializeError(err) });
      setEvents(prev); // reverte
      setError(t('admin.notificationPrefs.saveError'));
    }
  }

  /** Troca um canal de um evento e persiste. */
  function toggle(event: AdminNotificationEvent, channel: AdminNotificationChannel, checked: boolean): void {
    const next: ResolvedEvents = {
      ...events,
      [event]: { ...events[event], [channel]: checked },
    };
    void persist(next);
  }

  return (
    <Container
      header={
        <Header variant="h2" description={t('admin.notificationPrefs.description')}>
          {t('admin.notificationPrefs.title')}
        </Header>
      }
    >
      <SpaceBetween size="m">
        {webPush.available && !webPush.on ? (
          <Alert type="info">{t('admin.notificationPrefs.pushDisabledHint')}</Alert>
        ) : null}
        {error !== null ? <Alert type="error">{error}</Alert> : null}
        <Table<Row>
          variant="embedded"
          loading={loading}
          loadingText={t('admin.notificationPrefs.loading')}
          items={ROWS}
          trackBy="event"
          columnDefinitions={[
            {
              id: 'event',
              header: t('admin.notificationPrefs.columns.event'),
              cell: (row): string => t(`admin.notificationPrefs.events.${row.event}`),
            },
            {
              id: 'email',
              header: t('admin.notificationPrefs.columns.email'),
              cell: (row): JSX.Element => (
                <Toggle
                  checked={events[row.event].email}
                  ariaLabel={`${t('admin.notificationPrefs.columns.email')} — ${t(`admin.notificationPrefs.events.${row.event}`)}`}
                  onChange={({ detail }): void => {
                    toggle(row.event, 'email', detail.checked);
                  }}
                />
              ),
            },
            {
              id: 'push',
              header: t('admin.notificationPrefs.columns.push'),
              cell: (row): JSX.Element => (
                <Toggle
                  checked={events[row.event].push}
                  ariaLabel={`${t('admin.notificationPrefs.columns.push')} — ${t(`admin.notificationPrefs.events.${row.event}`)}`}
                  onChange={({ detail }): void => {
                    toggle(row.event, 'push', detail.checked);
                  }}
                />
              ),
            },
          ]}
          empty={<Box color="text-body-secondary">{t('admin.notificationPrefs.loading')}</Box>}
        />
      </SpaceBetween>
    </Container>
  );
}
